<script lang="ts">
  import { LogIn, UserPlus, Lock, XCircle } from '@lucide/svelte';
  
  interface Props {
    email: string;
    password: string;
    name: string;
    isRegistering: boolean;
    loginError: string;
    isPending: boolean;
    onsubmit: (e: SubmitEvent) => void;
  }

  let {
    email = $bindable(),
    password = $bindable(),
    name = $bindable(),
    isRegistering = $bindable(),
    loginError = $bindable(),
    isPending,
    onsubmit
  }: Props = $props();

  function toggleMode() {
    isRegistering = !isRegistering;
    loginError = '';
  }
</script>

<div class="flex-1 flex items-center justify-center p-4 md:p-8 bg-ambient-flow min-h-screen relative overflow-hidden">
  
  <!-- Subtle decorative ambient blobs -->
  <div class="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-3xl -z-10 animate-pulse"></div>
  <div class="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/15 rounded-full blur-3xl -z-10 animate-pulse" style="animation-delay: 2s;"></div>

  <div class="card w-full max-w-md bg-base-100/40 backdrop-blur-2xl border border-white/10 dark:border-white/5 shadow-2xl rounded-3xl overflow-hidden transition-all duration-300 hover:shadow-accent/5">
    <div class="card-body p-8 sm:p-10 space-y-6">
      
      <!-- Brand Logo / Identity Header -->
      <div class="text-center space-y-3">
        <div class="mx-auto w-14 h-14 rounded-2xl bg-gradient-to-tr from-accent to-accent-hover flex items-center justify-center shadow-lg shadow-accent/20 text-text-inverse">
          <Lock class="w-7 h-7 stroke-[1.5]" />
        </div>
        <div class="space-y-1">
          <h1 class="text-3xl font-extrabold tracking-tight brand-font-safe text-accent">
            Aatmanova
          </h1>
          <p class="text-xs uppercase tracking-widest text-base-content/50 font-bold">
            {isRegistering ? 'Create Customer Account' : 'Customer Portal'}
          </p>
        </div>
      </div>

      {#if loginError}
      <div role="alert" class="alert alert-error py-3 px-4 text-xs font-semibold flex items-center gap-2.5 rounded-xl text-white">
          <XCircle class="shrink-0 w-5 h-5" />
          <span>{loginError}</span>
        </div>
      {/if}

      <form {onsubmit} class="space-y-5">
        {#if isRegistering}
          <div class="form-control w-full">
            <label for="name" class="label pb-1.5">
              <span class="label-text text-[10px] font-bold text-base-content/60 uppercase tracking-wider">Full Name</span>
            </label>
            <input
              type="text"
              id="name"
              required
              bind:value={name}
              placeholder="Your Name"
              class="input input-bordered w-full bg-base-200/50 rounded-xl"
            />
          </div>
        {/if}

        <div class="form-control w-full">
          <label for="email" class="label pb-1.5">
            <span class="label-text text-[10px] font-bold text-base-content/60 uppercase tracking-wider">Email Address</span>
          </label>
          <input
            type="email"
            id="email"
            required
            bind:value={email}
            placeholder="you@example.com"
            class="input input-bordered w-full bg-base-200/50 rounded-xl"
          />
        </div>

        <div class="form-control w-full">
          <label for="password" class="label pb-1.5">
            <span class="label-text text-[10px] font-bold text-base-content/60 uppercase tracking-wider">Password</span>
          </label>
          <input
            type="password"
            id="password"
            required
            bind:value={password}
            placeholder="••••••••"
            class="input input-bordered w-full bg-base-200/50 rounded-xl"
          />
        </div>

        <button
          type="submit"
          disabled={isPending}
          class="w-full bg-gradient-to-r from-accent to-accent-hover text-text-inverse font-bold border-none active:scale-[0.98] transition-all mt-4 py-3 rounded-xl flex items-center justify-center gap-2 shadow-lg shadow-accent/15 hover:shadow-accent/25 cursor-pointer text-white h-12"
        >
          {#if isPending}
            <span class="loading loading-spinner loading-xs"></span>
            Processing...
          {:else if isRegistering}
            <UserPlus class="w-4 h-4 text-text-inverse" /> Create Account
          {:else}
            <LogIn class="w-4 h-4 text-text-inverse" /> Log In
          {/if}
        </button>
      </form>

      <div class="text-center pt-2">
        <button
          type="button"
          onclick={toggleMode}
          class="text-xs font-semibold text-accent hover:underline cursor-pointer"
        >
          {isRegistering ? 'Already have an account? Log In' : 'New customer? Create an account'}
        </button>
      </div>

    </div>
  </div>
</div>
